import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PefApiData, PefSchema } from '@sportello-balneari/pef-editor';
import { EMPTY, catchError, exhaustMap, take, takeWhile, timer } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { PERSISTENCY_MODULE_CONFIG } from '../persistency-module-config';
import {
  PefReportParameters,
  TenderFormAnswerAttachment,
  TenderFormAnswerEvaluationProgress,
  TenderFormEvaluation,
} from './interfaces';

/**
 * How often, and for how long, an attachment is followed after its indexing was queued. Content
 * Understanding on a long scan takes minutes, and the backend gives a file 5 minutes to settle in
 * the vector store on top of that — past the budget the badge simply stays "In corso".
 */
const INDEXING_POLL_INTERVAL_MS = 3000;
const MAX_INDEXING_POLLS = 200;

@Injectable({
  providedIn: 'root',
})
export class CompilingApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(PERSISTENCY_MODULE_CONFIG);

  private readonly tenderFormsAnswersUrl = `${this.config.apiBaseUrl}/api/app/TenderFormAnswers`;
  private readonly pefReportUrl = `${this.config.apiBaseUrl}/api/app/pefreport`;

  uploadFile(request: { attachment: File }) {
    const { attachment } = request;
    const formData = new FormData();
    formData.append('file', attachment);
    formData.append('ContentType', attachment.type);
    formData.append('ContentDisposition', '');
    formData.append('Headers.propertyName*', '');
    formData.append('Length', '1');
    formData.append('Name', attachment.name);
    formData.append('FileName', attachment.name);

    return this.http.post<{
      blobName: string;
      downloadUrl: string;
    }>(`${this.config.apiBaseUrl}/api/TenderFormAnswersAttachments`, formData);
  }

  uploadAndIndex(request: {
    file: File;
    tenderFormAnswerId: string;
    envelope: string;
    category: string;
  }): Observable<TenderFormAnswerAttachment> {
    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('tenderFormAnswerId', request.tenderFormAnswerId);
    formData.append('envelope', request.envelope);
    formData.append('category', request.category);

    return this.http.post<TenderFormAnswerAttachment>(
      `${this.config.apiBaseUrl}/api/TenderFormAnswersAttachments/upload-and-index-async`,
      formData,
    );
  }

  /**
   * One attachment as it stands right now. Used to follow `indexingStatus`
   * after something server-side has put a file in place: the caller holds an
   * id and nothing else.
   */
  getAttachment(attachmentId: string): Observable<TenderFormAnswerAttachment> {
    return this.http.get<TenderFormAnswerAttachment>(
      `${this.config.apiBaseUrl}/api/TenderFormAnswersAttachments/${attachmentId}`,
    );
  }

  deleteAttachment(attachmentId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.config.apiBaseUrl}/api/TenderFormAnswersAttachments/${attachmentId}`,
    );
  }

  reindexAttachment(
    attachmentId: string,
  ): Observable<TenderFormAnswerAttachment> {
    return this.http.post<TenderFormAnswerAttachment>(
      `${this.config.apiBaseUrl}/api/TenderFormAnswersAttachments/${attachmentId}/reindex-async`,
      {},
    );
  }

  /**
   * Follows an attachment whose indexing has been queued, emitting it on every poll until its
   * status leaves `InProgress` (that last state included). Both `uploadAndIndex` and
   * `reindexAttachment` only queue the work — they go through Content Understanding, which is far
   * too slow to wait for inside the request — so this is how the badge reaches its verdict.
   *
   * A failed poll ends the watch quietly rather than erroring: the upload or reindex itself
   * succeeded, and reporting it as failed would be wrong. The badge stays "In corso" until reload.
   */
  watchIndexing(attachmentId: string): Observable<TenderFormAnswerAttachment> {
    return timer(INDEXING_POLL_INTERVAL_MS, INDEXING_POLL_INTERVAL_MS).pipe(
      take(MAX_INDEXING_POLLS),
      // Not `switchMap`: a poll slower than the interval would be cancelled by the next one forever.
      exhaustMap(() => this.getAttachment(attachmentId)),
      takeWhile(
        (attachment) => attachment.indexingStatus === 'InProgress',
        true,
      ),
      catchError(() => EMPTY),
    );
  }

  getDownloadLink(blobName: string) {
    return this.http.post<{ downloadUrl: string }>(
      `${this.config.apiBaseUrl}/api/TenderFormAnswersAttachments/download-link`,
      { blobName },
    );
  }

  getPefSchema(tenderFormAnswerId: string, startYear: number, endYear: number) {
    const url = `${this.tenderFormsAnswersUrl}/${tenderFormAnswerId}/pef-schema`;

    let params = new HttpParams();

    params = params.append('startYear', startYear.toString());
    params = params.append('endYear', endYear.toString());

    return this.http.get<PefSchema>(url, { params });
  }

  getResult(tenderFormAnswerId: string) {
    return this.http.get<TenderFormEvaluation>(
      `${this.tenderFormsAnswersUrl}/${tenderFormAnswerId}/result`,
    );
  }

  requestEvaluation(tenderFormAnswerId: string) {
    return this.http.post(
      `${this.tenderFormsAnswersUrl}/${tenderFormAnswerId}/request-evaluation`,
      {},
    );
  }

  getEvaluationProgress(tenderFormAnswerId: string) {
    return this.http.get<TenderFormAnswerEvaluationProgress>(
      `${this.tenderFormsAnswersUrl}/${tenderFormAnswerId}/evaluation-progress`,
    );
  }

  calculatePef(
    tenderFormAnswerId: string,
    startYear: number,
    endYear: number,
    pefInput: PefApiData,
  ) {
    const url = `${this.tenderFormsAnswersUrl}/${tenderFormAnswerId}/calc-pef`;

    return this.http.post<PefApiData>(url, {
      startYear,
      endYear,
      pefInput,
    });
  }

  generatePefReport(
    tenderFormAnswerId: string,
    reportParams: PefReportParameters,
  ): Observable<{ blobName: string }> {
    return this.http.post<{ blobName: string }>(
      `${this.pefReportUrl}/${tenderFormAnswerId}/generate`,
      reportParams,
    );
  }

  getPefReportDownloadLink(
    tenderFormAnswerId: string,
  ): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(
      `${this.pefReportUrl}/${tenderFormAnswerId}/download`,
    );
  }

  generateDescrizione(
    tenderFormAnswerId: string,
  ): Observable<{ descrizioneIniziativaTesto: string }> {
    return this.http.get<{ descrizioneIniziativaTesto: string }>(
      `${this.pefReportUrl}/${tenderFormAnswerId}/generate-descrizione`,
    );
  }

  compileAnswer(
    tenderFormAnswerId: string,
    widgetId: string,
    userInstructions?: string,
  ): Observable<CompileAnswerResponse> {
    return this.http.post<CompileAnswerResponse>(
      `${this.tenderFormsAnswersUrl}/${tenderFormAnswerId}/compile-answer`,
      { widgetId, userInstructions },
    );
  }

  /**
   * Bulk counterpart of `compileAnswer`: one LLM call per compilable question,
   * four at a time, in a single request. A form with a few dozen questions can
   * take several minutes, so this deliberately carries no timeout — Angular's
   * `HttpClient` sets none by default and no interceptor adds one. Cancel it by
   * unsubscribing (that aborts the underlying request); do not race it with a
   * timer.
   *
   * Nothing is persisted: the response only *proposes* answers, exactly like
   * the single-question endpoint. The caller applies them locally and the
   * applicant still has to save.
   */
  compileAllAnswers(
    tenderFormAnswerId: string,
    request: CompileAllAnswersRequest = {},
  ): Observable<CompileAllAnswersResponse> {
    return this.http.post<CompileAllAnswersResponse>(
      `${this.tenderFormsAnswersUrl}/${tenderFormAnswerId}/compile-all-answers`,
      request,
    );
  }

  /**
   * Whether `compileAllAnswers` is reachable for this answer. Detached
   * evaluations run against their own endpoints and have no bulk counterpart,
   * so the variant that serves them turns this off and the button stays hidden.
   */
  readonly supportsBulkCompile: boolean = true;
}

/**
 * One compiled suggestion. Byte-for-byte the same shape from the single-question
 * endpoint and from each entry of the bulk one, so both apply it the same way
 * (`applyCompiledAnswer`). Fields are nullable: the backend sends `null` for the
 * half that does not apply to the question type.
 */
export interface CompileAnswerResponse {
  questionType?: 'Tabular' | 'Descriptive' | null;
  selectedValue?: string | null;
  /** Tabular only: the human-readable label of `selectedValue`. */
  selectedLabel?: string | null;
  answer?: string | null;
  justification?: string | null;
  /**
   * MultiRadioButton only: one pick per group of the widget, in widget
   * order, with `selectedValue` null for a group the documents do not cover.
   * `selectedValue`/`answer` above are null when this is set.
   */
  groups?: CompiledGroupAnswer[] | null;
  /**
   * MultiplierRadioButton only: the quantity for the option `selectedValue`
   * names (an option key). 0 for the default option.
   */
  numericValue?: number | null;
}

export interface CompiledGroupAnswer {
  /** The group's `key` — what the stored answer object is keyed by. */
  groupId: string;
  groupLabel?: string | null;
  selectedValue?: string | null;
  selectedLabel?: string | null;
}

export interface CompileAllAnswersRequest {
  /**
   * `false` (the default) compiles only the questions whose answer is still
   * empty; `true` recompiles every compilable question, overwriting answers the
   * applicant already gave.
   */
  overwriteExisting?: boolean;
  /** Per-question instructions, keyed by widget id. Absent widgets get none. */
  userInstructions?: { [widgetId: string]: string };
}

export type CompileAllSkipReason = 'AlreadyAnswered' | 'UnsupportedWidgetType';

export interface CompileAllAnswersResponse {
  compiled: { widgetId: string; answer: CompileAnswerResponse }[];
  skipped: { widgetId: string; reason: CompileAllSkipReason }[];
  failed: { widgetId: string; message: string }[];
}
